import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, TextField, Button, Typography, IconButton,
  InputAdornment, CircularProgress, Alert, Card, CardContent,
} from '@mui/material';
import { Visibility, VisibilityOff, ElectricBolt, Email, Lock } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import logo from '../assets/logo.png';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm]         = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { 
      setError('Please enter email and password.'); 
      return; 
    }
    
    setLoading(true);
    setError('');
    const res = await InvokeApi(Config.base_url + apiList.adminLogin, { 
      email: form.email, 
      password: form.password 
    });
    setLoading(false);
    
    if (res?.code === '200' || res?.status === 'success' || res?.token) {
      const data = res.data ?? res;
      login({ 
        token: data.token, 
        role: data.role, 
        adminId: data.adminId ?? data.admin_id, 
        name: data.name 
      });
      navigate('/dashboard');
    } else {
      setError(res?.message || 'Invalid credentials. Please try again.');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      background: 'linear-gradient(135deg, #0A2540 0%, #0d47a1 50%, #1565c0 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Decorative background circles */}
      {[
        { w: 350, h: 350, top: '-10%', left: '-8%' },
        { w: 200, h: 200, top: '65%', left: '80%' },
        { w: 450, h: 450, top: '-15%', left: '65%' },
        { w: 150, h: 150, top: '75%', left: '-3%' },
      ].map((s, i) => (
        <Box key={i} sx={{
          position: 'absolute',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          width: s.w,
          height: s.h,
          top: s.top,
          left: s.left,
        }} />
      ))}

      <Grid container sx={{ position: 'relative', zIndex: 1, maxWidth: 1200, mx: 'auto', width: '100%' }}>
        {/* Left Side - Branding */}
        <Grid item xs={12} md={6} sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 4, md: 6 },
          color: '#fff',
        }}>
          <Box sx={{ maxWidth: 450, textAlign: { xs: 'center', md: 'left' } }}>
            <Box sx={{
              width: 300,
              height: 150,
              borderRadius: 1,
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              mx: { xs: 'auto', md: 0 },
              p: 0,
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}>
              <img src={logo} alt="Ridexcity" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }} />
            </Box>
            {/* <Typography variant="h3" fontWeight={800} letterSpacing={1} sx={{ mb: 2 }}>
              RIDEXCITY
            </Typography> */}
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', mb: 3, lineHeight: 1.6 }}>
              Smart EV Fleet Management Platform
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
              {[
                { icon: <ElectricBolt />, text: 'Real-time Vehicle Tracking' },
                { icon: <ElectricBolt />, text: 'Comprehensive User Management' },
                { icon: <ElectricBolt />, text: 'Advanced Analytics & Reports' },
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#00C6FF',
                  }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.9)' }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        {/* Right Side - Login Form */}
        <Grid item xs={12} md={6} sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 4 },
        }}>
          <Card sx={{
            width: '100%',
            maxWidth: 430,
            borderRadius: 4,
            boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
            background: 'rgba(255,255,255,0.98)',
          }}>
            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" fontWeight={800} color="#0A2540" letterSpacing={0.5}>
                  Admin Panel
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Sign in to access your dashboard
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  sx={{ mb: 2.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: '#0A2540' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  required
                  sx={{ mb: 3.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#0A2540' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPass(!showPass)} edge="end">
                          {showPass ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.6,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: 16,
                    background: 'linear-gradient(135deg, #0A2540, #1565c0)',
                    boxShadow: '0 8px 24px rgba(10,37,64,0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #071a2d, #0d47a1)',
                      boxShadow: '0 12px 32px rgba(10,37,64,0.5)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                </Button>
              </form>

              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
                © 2026 Ridexcity · Secure Admin Access
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
